/**
 * Strategiewahl in Abschnitt 1: drei Systemprofile als Karten, „Strategielogik erklären“ aufgeklappt
 * darunter, „Regelwerk vergleichen“ und „Kalibrierungsdaten anzeigen“ als eigene Aufklapper. Fehlt
 * ein Profil oder ist die aktive Version nicht ladbar, ist die Wahl gesperrt.
 */
import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PricingStrategyProfileResponse } from '@/shared/api';
import { ProfileCalibrationDetails } from './ProfileCalibrationDetails';
import { ProfileChoiceCards } from './ProfileChoiceCards';
import { ProfileExplanation } from './ProfileExplanation';
import { buildProfileComparisonSections } from './profileComparisonRows';
import { StrategyProfileRuleDetails } from './StrategyProfileRuleDetails';

const order = ['MaximizeOccupancy', 'Balanced', 'MaximizeRevenue'] as const;

interface Props {
  profiles: PricingStrategyProfileResponse[];
  selectedProfileVersionId: string | null;
  activeProfileVersionId: string | null;
  activeProfile?: PricingStrategyProfileResponse;
  activeProfileProblem?: string | null;
  onRetryActiveProfile?: () => void;
  error?: string;
  onSelect: (versionId: string) => void;
}

export function StrategyProfileExplorer(props: Props) {
  const { t } = useTranslation();
  const [explain, setExplain] = useState(false);
  const panelId = useId();
  const current = useMemo(
    () => order.map((strategy) => props.profiles.find((profile) => profile.strategy === strategy)),
    [props.profiles],
  );
  if (current.some((profile) => !profile)) {
    return <Blocked text={t('admin.dynamicPricing.profiles.unavailable')} />;
  }
  const ordered = current as PricingStrategyProfileResponse[];
  const selected = ordered.find((profile) => profile.versionId === props.selectedProfileVersionId);
  const pinned =
    ordered.find((profile) => profile.versionId === props.activeProfileVersionId) ??
    props.activeProfile;
  if (props.activeProfileVersionId && !pinned) {
    return (
      <Blocked
        text={t('admin.dynamicPricing.profiles.staleUnavailable')}
        reason={props.activeProfileProblem}
        onRetry={props.onRetryActiveProfile}
      />
    );
  }
  const explained = selected ?? pinned ?? ordered[0]!;
  const stale =
    props.activeProfileVersionId &&
    !ordered.some((profile) => profile.versionId === props.activeProfileVersionId);
  return (
    <div className="@container flex min-w-0 flex-col gap-3">
      <div className="flex items-baseline gap-2.5 pt-1.5">
        <span className="text-ink-soft grow font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">
          Strategie
        </span>
        <button
          type="button"
          aria-expanded={explain}
          aria-controls={panelId}
          onClick={() => setExplain(!explain)}
          className="text-forest min-h-11 text-[13px] font-bold"
        >
          Strategielogik erklären
        </button>
      </div>
      {stale && (
        <p className="bg-amber-tint text-ink rounded-control p-3 text-sm">
          {t('admin.dynamicPricing.profiles.staleActive', { version: pinned!.versionNumber })}
        </p>
      )}
      <ProfileChoiceCards
        profiles={ordered}
        selectedProfileVersionId={props.selectedProfileVersionId}
        activeProfileVersionId={props.activeProfileVersionId}
        onSelect={props.onSelect}
      />
      {props.error && <p className="text-sealwax text-sm">{props.error}</p>}
      {explain && (
        <ProfileExplanation id={panelId} profile={explained} onClose={() => setExplain(false)} />
      )}
      <StrategyProfileRuleDetails
        profiles={ordered}
        sections={buildProfileComparisonSections(ordered)}
        selectedProfileVersionId={props.selectedProfileVersionId}
      />
      <ProfileCalibrationDetails profile={explained} />
    </div>
  );
}

function Blocked({
  text,
  reason,
  onRetry,
}: {
  text: string;
  reason?: string | null;
  onRetry?: () => void;
}) {
  return (
    <div
      role="alert"
      className="bg-sealwax-tint text-sealwax rounded-control flex flex-wrap items-center gap-2 p-3 text-sm"
    >
      <span className="min-w-0 grow">
        {text}
        {reason && <span className="text-ink block">{reason}</span>}
      </span>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-pill bg-card text-ink min-h-11 px-4 text-[13px] font-bold"
        >
          Erneut laden
        </button>
      )}
    </div>
  );
}
